<script>
export default {
  page: {
    title: 'Privacy Policy Page...',
    meta: [{ name: 'description', content: 'Privacy Policy page...' }],
  },
}
</script>

<template>
  <div :class="$style.pageContainer">
    <p :class="['pb-2', $style.subject]">Privacy Policy</p>
    <p :class="$style.date">Dated: [03/25/2025]</p>
    <div :class="$style.pageText">
      <p :class="['pb-4', $style.contentText]">
        We are committed to maintaining the privacy and security of your
        Personal Information, and we appreciate that medical information is
        sensitive. This Privacy Policy describes how HealthBookplus, Inc.
        ("HealthBook+," "we" or "us") may collect, use and share your Personal
        Information, including your Health Information, when you visit the
        HealthBook+ website at
        <a href="https://www.healthbookplus.ai">www.healthbookplus.ai</a> or use
        the HealthBook+ web & mobile application (the “Application”) to access,
        upload, or share your Health Information (collectively, the "Services").
      </p>
      <p :class="['pb-4', $style.contentText]">
        The Application is a personal health and wellness information retrieval
        and management service that allows individuals to consolidate certain
        health and wellness information ("Health Information") through the
        Application and share information with others (including family members,
        doctors and other thirdparty providers). The Application is provided to
        you by HealthBook+ as an aid to assist you to own your own Health.
      </p>
      <p :class="['pb-4', $style.contentText]">
        “Personal Information” is any information that directly or indirectly
        identifies you, such as your name, email address, or other identifying
        information about you, or that would, when combined with other
        information you provide or that is broadly available, be likely to
        identify you. This Privacy Policy does not address Personal Information
        that you provide to us in other contexts (for example, through a
        business relationship unrelated to the Services). This Privacy Policy is
        also subject to our End User Terms of Service (“Terms”). Capitalized
        words not defined in this Privacy Policy will have the meaning assigned
        to them in our Terms.
      </p>
    </div>
    <div>
      <p :class="['pb-0', $style.titleText]">Acceptance of Privacy Policy</p>
      <p :class="['pb-4', $style.contentText]">
        By using our Services, you are agreeing to the terms of this Privacy
        Policy. If you do not agree to the terms of this Privacy Policy, please
        do not use the Services. Your continued use of the Services after we
        make changes to this Privacy Policy will mean that you agree to those
        changes.
      </p>
    </div>
    <div>
      <p :class="$style.titleText">Collection of Personal Information</p>
      <div>
        <p :class="['pb-0', $style.subtitleText]"
          >Through our Mobile app and Website</p
        >
        <p :class="['pb-4', $style.contentText]">
          We collect your Personal Information through our website when you
          choose to provide that information, such as when you:
        </p>
        <ul :class="['pb-4', 'pl-10', $style.contentText]">
          <li> submit information to our website and mobile app,</li>
          <li> register on our website and mobile app,</li>
          <li>
            respond to our communications (for example, when you respond to an
            email from us), or
          </li>
          <li>
            participate in another one of our website or mobile app features</li
          >
        </ul>
        <p :class="['pb-4', $style.contentText]">
          The Personal Information that we collect will depend on the context of
          our interaction with you. For example, when you sign up or register on
          our mobile app or website, we may ask you for your name, email
          address, mailing address, phone number or other information, but you
          may still use our mobile app and website without providing that
          information.
        </p>
      </div>
      <div>
        <p :class="['pb-0', $style.subtitleText]">Through the Application</p>
        <p :class="['pb-4', $style.contentText]">
          Our Application collects your Personal Information when you create a
          new account, including your name, date of birth, gender, address,
          phone number, and email address.
        </p>
        <p :class="['pb-4', $style.contentText]">
          In addition, we collect and keep your Health Information and sync your
          Health Information from your Providers or other authorized third-party
          sources.
        </p>
      </div>
      <div>
        <p :class="$style.subtitleText">
          Automatically Collected Information & Anonymous Information
        </p>
        <p :class="['pb-4', $style.contentText]">
          <span :class="$style.contentTitleText">Aggregated Data</span>. We
          collect aggregate inquiries for internal reporting and also count,
          track, and aggregate each visitor's activity into our analysis of
          general traffic flow at our website. To make sure this happens, we may
          merge information about you into aggregated group data. In some cases,
          we may remove personal identifiers from Personal Information and
          maintain it in aggregate form that may later be combined with other
          information to generate anonymous, aggregated statistical information.
          That anonymous group data may be shared with our affiliates, business
          partners, service providers and/or vendors as described more fully
          below; if it is shared, we will not disclose your individual identity.
        </p>
        <p :class="['pb-4', $style.contentText]">
          <span :class="$style.contentTitleText"
            >Web Server Logs and IP Addresses</span
          >. An Internet Protocol ("IP") address is a number that automatically
          identifies the computer/machine you have used to access the Internet.
          The IP address enables our server to send you the web pages that you
          want to visit, and it may identify the server owned by your Internet
          Service Provider. We may use IP addresses to conduct website analyses
          and performance reviews and to administer our website.
        </p>
        <p :class="['pb-4', $style.contentText]">
          <span :class="$style.contentTitleText">Location information</span>.
          When you access the Services on a location-enabled device, we may
          collect and process information about your actual location, like GPS
          signals sent by a mobile device. We also may use various technologies
          to determine location, such as sensor data from your device that may,
          for example, provide information on nearby Wi-Fi access points and
          cell towers.
        </p>
        <p :class="['pb-4', $style.contentText]">
          <span :class="$style.contentTitleText"
            >Unique application numbers</span
          >. Certain versions of the Application may include a unique
          application number. This number and information about your
          installation (for example, the operating system type and application
          version number) may be sent to us when you install or uninstall an
          application or utilize that or when that application periodically
          contacts our servers, such as for automatic updates.
        </p>
        <p :class="['pb-4', $style.contentText]">
          <span :class="$style.contentTitleText">Local storage</span>. We may
          collect and store information on your device using mechanisms such as
          browser web storage (including HTML 5) and application data caches.
        </p>
        <p :class="['pb-4', $style.contentText]">
          <span :class="['pb-0', $style.contentTitleText]"
            >Cookies and Web Beacons</span
          >. Cookies are pieces of information that a website transfers to a
          user's computer for purposes of storing information about the user's
          preferences. Many websites use cookies as a standard practice to
          provide useful features when a user visits the website, and most web
          browsers are set up to accept cookies. We use cookies to improve your
          online experience when visiting the website. You can set your browser
          to refuse cookies, but some portions of our website may not work
          properly if you refuse cookies. Some of our website's web pages may
          use web beacons along with cookies to compile aggregate statistics
          about website usage. A web beacon is an electronic image (also
          referred to as an "action tag," "single-pixel," or "clear GIF") that
          is commonly used to track the traffic patterns of users from one web
          page to another in order to maximize web traffic flow and to otherwise
          analyze the effectiveness of our website. Some web beacons may be
          unusable if you elect to reject their associated cookies.
        </p>
      </div>
    </div>
    <div>
      <p :class="['pb-0', $style.titleText]">Use of Personal Information</p>
      <p :class="['pb-4', $style.contentText]">
        We take your privacy seriously. We use the information that we collect
        about you to provide, maintain, and improve the services and products
        that we provide to you. We do not sell information that can be used to
        identify you to any third parties and only use it for legitimate
        business purposes.
      </p>
      <p :class="['pb-4', $style.contentText]">
        We use your Personal Information for the following purposes, or may use
        it for these purposes in the future:
      </p>
      <ul :class="['pb-4', 'pl-10', $style.contentText]">
        <li> To respond to questions that you may have. </li>
        <li> To provide products or services you request. </li>
        <li>
          To inform you of updates regarding HealthBook+ or services or products
          that may be of interest to you.
        </li>
        <li>
          To provide administrative notices or communications applicable to your
          use of the Services.
        </li>
        <li>
          To manage your participation in events and other promotions where you
          have signed up for such events and promotions.
        </li>
        <li>
          To operate, evaluate and improve our business and the products and
          services we offer.
        </li>
        <li>
          To analyze and enhance our marketing communications and strategies.
        </li>
        <li>
          To analyze trends and statistics regarding visitors’ use of the
          Services including mobile applications and social media assets.
        </li>
        <li>
          To protect against and prevent fraud, unauthorized transactions,
          claims and other liabilities.
        </li>
        <li> To improve our customer service. </li>
        <li>
          To respond to and support customers regarding their use of the
          Services.
        </li>
        <li> To comply with all applicable legal requirements. </li>
      </ul>
      <p :class="['pb-4', $style.contentText]">
        We also may use the information we obtain about you in other ways for
        which we provide notice at the time of collection. We may ask for your
        consent before using information for a purpose other than those set out
        in this Privacy Policy.
      </p>
    </div>
    <div>
      <p :class="$style.titleText">Sharing Personal Information</p>
      <div>
        <p :class="['pb-0', $style.subtitleText]"
          >With Our Vendors/Contractors</p
        >
        <p :class="['pb-4', $style.contentText]">
          In using your Personal Information for the above purposes, we may
          disclose your Personal Information to our vendors and other
          contractors who perform various functions on our behalf. We require
          these third parties to agree in writing to safeguard your Personal
          Information against improper disclosure and in accordance with
          applicable laws.
        </p>
      </div>
      <div>
        <p :class="['pb-0', $style.subtitleText]">Health Information</p>
        <p :class="['pb-4', $style.contentText]">
          When you use the Application, you may authorize us to share Health
          Information with your Providers or other Authorized Individuals. You
          control how this Health Information is shared. Please view our Terms
          for information about the types of access you may grant to others.
        </p>
      </div>
      <div>
        <p :class="['pb-0', $style.subtitleText]">Account Information</p>
        <p :class="['pb-4', $style.contentText]">
          Certain areas and features of our website are available to you without
          registration. However, other features of our website or the Services
          may require registration, which involves giving us your email address,
          a password and a username (the "Account Information"). In order to
          fully benefit from our Services, you also must provide your
          third-party health portal credentials ("Portal Credentials") to allow
          us to access your health data at those other healthcare providers'
          organizations ("Portal Information") for your use.
        </p>
      </div>
      <div>
        <p :class="['pb-0', $style.subtitleText]">Other Disclosures</p>
        <p :class="['pb-4', $style.contentText]">
          We also may provide information about you to other third parties with
          your consent or for one of the purposes above, e.g., following a
          subpoena or other legal process, or to a regulatory agency in order to
          protect against fraud or investigate a criminal or civil matter, or
          when we believe in good faith that disclosure is reasonably necessary
          to protect our rights and interests, third parties, or the public at
          large.
        </p>
      </div>
      <div>
        <p :class="['pb-0', $style.subtitleText]">Business Transfers</p>
        <p :class="['pb-4', $style.contentText]">
          If we or any of our affiliates is involved in a merger, acquisition,
          asset sale, joint venture or other, similar transaction, your
          information may be transferred to, or accessed by, a successor or
          other entity and may be subject to that entity’s privacy policies,
          which may vary from this Privacy Policy. We may transfer or allow
          access to Personal Information as part of the transaction and in
          review or preparation for it. More specifically, in the event
          HealthBook+ (i) engages in one or more asset sales or purchases, (ii)
          is acquired, or substantially all of its assets are acquired, (iii)
          engages in one or more business combinations, mergers or acquisitions,
          or (iv) is subject to an event of bankruptcy, you acknowledge and
          agree that user information comprises an asset of HealthBook+ and that
          the same is subject to assignment, transfer and/or acquisition by a
          third party. Accordingly, and notwithstanding anything to the contrary
          in this Privacy Policy, you acknowledge and agree that such transfers
          may occur, and that any acquirer of HealthBook+_may continue to
          subsequently use your Personal Information, and that such subsequent
          use may not be consistent with this Privacy Policy.
        </p>
      </div>
      <div>
        <p :class="['pb-0', $style.subtitleText]">Terminated Accounts</p>
        <p :class="['pb-4', $style.contentText]">
          If your account is terminated by you or by us in accordance with our
          Terms, we will promptly delete the Personal Information in your
          account, with the exception of a disclosure log that records how you
          shared the information in your account with third parties. The
          disclosure log will be maintained only for so long as is necessary for
          our business purposes and will be deleted as soon as practicable, in
          accordance with our record retention policies. You may close your
          account and request deletion of your data at any time by contacting us
          at privacy@healthbookplus.com
        </p>
      </div>
      <div>
        <p :class="['pb-0', $style.subtitleText]"
          >Anonymous and Aggregated Information</p
        >
        <p :class="['pb-4', $style.contentText]">
          We may share that anonymous and aggregated data about you for any
          lawful purpose, including:
        </p>
        <ul :class="['pb-4', 'pl-10', $style.contentText]">
          <li>
            With organizations approved by us that conduct research into health;
          </li>
          <li>
            With users of the Services for purposes of comparison of their
            personal health situations relative to others; and
          </li>
          <li> To improve our Services. </li>
        </ul>
      </div>
    </div>
    <div>
      <p :class="['pb-0', $style.titleText]"
        >Third-Party Links to Other Webpages/Tools</p
      >
      <p :class="['pb-4', $style.contentText]">
        The Services may contain links to webpages or tools operated by parties
        other than HealthBook+. We do not control such websites or tools and are
        not responsible for their contents or the privacy policies or other
        practices of such websites or service providers. Our inclusion of links
        to an outside website or tool does not imply any endorsement of the
        material on such websites or tools or any association with their
        operators. Further, it is up to you to take precautions to ensure that
        whatever links you select or software you download (whether from the
        Services or other websites) is free of such items as viruses, worms,
        trojan horses, defects and other items of a destructive nature. These
        websites and services may have their own privacy policies, which you
        will be subject to upon accessing the third party’s website. We strongly
        recommend you review the third party’s terms and policies.
      </p>
    </div>
    <div>
      <p :class="['pb-0', $style.titleText]">How We Protect Your Information</p>
      <p :class="['pb-4', $style.contentText]">
        HealthBook+ discloses Personal Information to those of its employees,
        contractors, agents and affiliated organizations that need to know that
        information in order to process it on HealthBook+’s behalf or to provide
        services. Some of those employees, contractors, agents and affiliated
        organizations may be located outside of your state, province or country.
        By using the Services or providing us information, you consent to the
        transfer of such information to them.
      </p>
      <p :class="['pb-4', $style.contentText]">
        We aim to maintain our services in a manner that protects information
        from accidental or malicious destruction. Because of this, we may not
        immediately delete residual copies of Personal Information that is on
        our active servers and may not remove information from our backup
        systems. If you believe that Personal Information we maintain about you
        is inaccurate, subject to applicable law, you may have the right to
        request that we correct or amend the information by contacting us as
        indicated below.
      </p>
      <p :class="['pb-4', $style.contentText]">
        HealthBook+ undertakes commercially reasonable efforts to guard against
        unauthorized access, use, alteration or destruction of Personal
        Information, which efforts you agree are sufficient, adequate and
        complete. In some cases, we cannot control, and are not responsible for,
        information that is shared about you. For example:
      </p>
      <p :class="['pb-4', $style.contentText]">
        <span :class="$style.contentTitleText">Information You Share</span>. The
        Services permit you to share information, including your Health
        Information, with others. We are not responsible for any unauthorized
        access, use, alteration, or destruction of your information caused by
        these third parties (including any Providers or Authorized Individuals
        you permit to access your Health Information). Remember that when you
        share information publicly, it may be indexable by search engines or
        otherwise further disclosed.
      </p>
      <p :class="['pb-4', $style.contentText]">
        <span :class="$style.contentTitleText">Social Media Accounts</span>.
        HealthBook+ maintains various social network accounts such as Facebook,
        Twitter or LinkedIn. Please note that comments and information that you
        leave in such settings are likely to be broadly visible and may not be
        controlled by HealthBook+. We are therefore not responsible for any
        information or messages you leave in such settings or for how such
        information may be used by others. Please review the terms of use and
        privacy policies of any social network platforms you use, as they will
        control your rights and responsibilities, and your use of those
        platforms is not covered by this Privacy Policy.
      </p>
    </div>
    <div>
      <p :class="['pb-0', $style.titleText]">Do Not Track</p>
      <p :class="['pb-4', $style.contentText]">
        Some browsers have a "Do Not Track" feature that lets you tell websites
        and online services that you do not want to have your online activities
        tracked. Such browser features and industry standards are not uniform,
        so our websites and online services do not respond to those signals.
      </p>
    </div>
    <div>
      <p :class="['pb-0', $style.titleText]">Children</p>
      <p :class="['pb-4', $style.contentText]">
        The Services are not intended for use by children. We do not
        intentionally gather Personal Information directly from individuals who
        are under the age of 13. We will only intentionally gather Personal
        Information about a child when a parent operates an account on behalf of
        that child. If a child has provided us with Personal Information, a
        parent or guardian of that child may contact us to have the information
        deleted from our records. If you believe that we might have any
        information from a child under age 13, please contact us at the email
        address below. If we learn that we have inadvertently collected the
        Personal Information of a child under 13, or equivalent minimum age
        depending on jurisdiction, we will take steps to delete the information
        as soon as possible.
      </p>
    </div>
    <div>
      <p :class="['pb-0', $style.titleText]">International Visitors</p>
      <p :class="['pb-4', $style.contentText]">
        The Services are intended for use in the United States, and we do not
        target or direct the Services to users in the European Union or
        otherwise outside of the United States. If you are visiting from the
        European Union or other regions that have laws governing data collection
        and use that may differ from U.S. law, please note that you are
        transferring information about you, including Personal Information, to a
        country and jurisdiction that does not have the same data protection
        laws as the European Union, and you consent to the transfer of
        information about you, including your Personal Information, to the U.S.,
        and the use and disclosure of information about you, including your
        Personal Information, as described in this Privacy Policy.
      </p>
    </div>
    <div>
      <p :class="['pb-0', $style.titleText]"
        >Changing or Deleting Your Information</p
      >
      <p :class="['pb-4', $style.contentText]">
        You may review and request changes to your Personal Information that we
        have collected.
      </p>
      <p :class="['pb-10', $style.contentText]">
        You may also request deletion of your Personal Information from our
        databases in order to close your account and prevent receipt of future
        communications. When you choose to delete your account, all of your
        Personal Information associated with the account is deleted immediately,
        and is no longer accessible by you or HealthBook+. The only data that is
        not deleted is data that we keep for audit and legal purposes.
      </p>
      <p :class="['pb-4', $style.contentText]">
        You may submit requests to change or delete your Personal Information
        using the following:
      </p>
      <p :class="['pb-4', $style.contentText]">
        You can send your request via email to [privacy@healthbookplus.com]
      </p>
      <p :class="['pb-4', $style.contentText]">
        You can mail your request to the following postal address:
      </p>
      <p :class="['pb-0', $style.contentText]"> Healthbook+ </p>
      <p :class="['pb-0', $style.contentText]"> 3513 Brighton Blvd STE 415 </p>
      <p :class="['pb-4', $style.contentText]"> Denver, CO 80216 </p>
    </div>
    <div>
      <p :class="['pb-0', $style.titleText]">Policy Updates</p>
      <p :class="['pb-4', $style.contentText]">
        HealthBook+ reserves the right to change this Privacy Policy from time
        to time and at any time, without notice, within the sole, independent,
        absolute and uncontrolled exercise of its discretion. By checking the
        Effective Date above, you can determine the date of our latest changes
        to this Privacy Policy. HealthBook+ encourages visitors to frequently
        check this page for any changes to its Privacy Policy. Your continued
        use of the Services after any change in this Privacy Policy will
        constitute your acceptance of, and consent and agreement to, such
        change.
      </p>
    </div>
    <div>
      <p :class="['pb-0', $style.titleText]">Questions</p>
      <p :class="['pb-4', $style.contentText]">
        If you have any questions about this Privacy Policy or about our
        handling of your Personal Information, please contact us at: [email
        privacy@healthbookplus.com]
      </p>
    </div>
  </div>
</template>

<style lang="scss" module>
.pageContainer {
  max-width: 1200px;
  padding: 20px 10px 20px 120px !important;
  margin: 0 !important;
}
.pageText {
  text-align: justify;
}
.subject {
  padding: 30px 0;
  margin: 0 !important;
  font-size: 42px;
  font-weight: bold;
  line-height: 50px;
}

.date {
  color: #484848;
}

.titleText {
  padding: 14px 0;
  margin: 0 !important;
  font-size: 18px;
  font-weight: bold;
  line-height: 22px;
  color: #254084;
}
.subtitleText {
  padding: 14px 0;
  margin: 0 !important;
  font-size: 16px;
  font-weight: bold;
  line-height: 22px;
  color: #254084;
}
.contentTitleText {
  margin: 0 !important;
  font-size: 14px;
  font-weight: bold;
  line-height: 21px;
  color: #254084;
}
.contentText {
  margin: 0 !important;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
}
@media screen and (max-width: 1200px) {
  .pageContainer {
    padding-left: 28px !important;
  }
}

@media screen and (max-width: 450px) {
  .pageContainer {
    padding-left: 20px !important;
  }
}
</style>
